import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Clock, AlertCircle, TrendingUp, CheckCircle, Calendar, FileText, Briefcase, Timer, Loader2, UserX, X, BarChart2 } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend } from 'recharts';
import { useTranslation } from 'react-i18next';
import { dashboardService, departmentService } from '../services';
import type { DashboardStats, DepartmentDistribution, AttendanceTrendItem, EmployeeListItem, LateEmployeeItem, EmployeeStatsSummary } from '../services';

type TimeFilter = 'daily' | 'weekly' | 'monthly';
type ModalType = 'total' | 'present' | 'absent' | 'late' | null;

const Dashboard = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [departments, setDepartments] = useState<DepartmentDistribution[]>([]);
  const [attendanceTrend, setAttendanceTrend] = useState<AttendanceTrendItem[]>([]);
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('daily');
  // 2026-08-15 (kullanici talebi): Panelde tarih araligi secimi yoktu.
  // Dolu ise donem butonlarinin yerine bu aralik kullanilir.
  const [ozelBas, setOzelBas] = useState<string>('');
  const [ozelBit, setOzelBit] = useState<string>('');
  // 2026-08-15 (kullanici): Native <input type="date"> tarayici diline gore
  // mm/dd/yyyy aciliyor ve elle yazmak zor. Turkce gg.aa.yyyy metin girisi
  // kullaniyoruz; ISO'ya cevirip backend'e oyle gonderiyoruz.
  const [basMetin, setBasMetin] = useState<string>('');
  const [bitMetin, setBitMetin] = useState<string>('');

  // Modal state
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [presentEmployees, setPresentEmployees] = useState<EmployeeListItem[]>([]);
  const [absentEmployees, setAbsentEmployees] = useState<EmployeeListItem[]>([]);
  const [lateEmployees, setLateEmployees] = useState<LateEmployeeItem[]>([]);
  const [employeeStats, setEmployeeStats] = useState<EmployeeStatsSummary | null>(null);

  // gg.aa.yyyy -> yyyy-aa-gg. Gecersizse bos doner (filtre uygulanmaz).
  const trTarihiIso = (metin: string): string => {
    const t = (metin || '').trim();
    const m = t.match(/^(\d{1,2})[.\/-](\d{1,2})[.\/-](\d{4})$/);
    if (!m) return '';
    const gun = Number(m[1]), ay = Number(m[2]), yil = Number(m[3]);
    if (gun < 1 || gun > 31 || ay < 1 || ay > 12) return '';
    const d = new Date(yil, ay - 1, gun);
    // 31.02 gibi gecersiz tarihleri ele
    if (d.getDate() !== gun || d.getMonth() !== ay - 1) return '';
    return `${yil}-${String(ay).padStart(2, '0')}-${String(gun).padStart(2, '0')}`;
  };

  // Yazarken noktalari otomatik koy: 15082026 -> 15.08.2026
  const tarihMaskele = (deger: string): string => {
    const rakam = deger.replace(/\D/g, '').substring(0, 8);
    if (rakam.length <= 2) return rakam;
    if (rakam.length <= 4) return `${rakam.slice(0, 2)}.${rakam.slice(2)}`;
    return `${rakam.slice(0, 2)}.${rakam.slice(2, 4)}.${rakam.slice(4)}`;
  };

  // 2026-08-15: Tek efekt — hem ilk acilis hem donem/tarih degisimi.
  // Onceden bos bagimlilikli ayri bir efekt daha vardi, acilista CIFT istek
  // atiyordu; ayrica ozel tarih secilince kartlar yenilenmiyordu.
  useEffect(() => {
    loadDashboardData();
    loadTrendData();
  }, [timeFilter, ozelBas, ozelBit]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [statsRes, deptRes, trendRes, empStatsRes] = await Promise.all([
        dashboardService.getStats(timeFilter, ozelBas || undefined, ozelBit || undefined),
        dashboardService.getDepartmentDistribution(),
        dashboardService.getAttendanceTrend(7),
        dashboardService.getEmployeeStatsSummary()
      ]);

      if (statsRes.success) setStats(statsRes.data);
      if (deptRes.success) setDepartments(deptRes.data);
      if (trendRes.success) setAttendanceTrend(trendRes.data);
      if (empStatsRes.success) setEmployeeStats(empStatsRes.data);

    } catch (err: any) {
      console.error('Dashboard load error:', err);
      setError(err.response?.data?.message || t('dashboard.errorLoadingData'));
    } finally {
      setLoading(false);
    }
  };

  const loadTrendData = async () => {
    try {
      const days = timeFilter === 'daily' ? 7 : timeFilter === 'weekly' ? 28 : 90;
      const trendRes = await dashboardService.getAttendanceTrend(days);
      if (trendRes.success) setAttendanceTrend(trendRes.data);
    } catch (err) {
      console.error('Trend load error:', err);
    }
  };

  const openModal = async (type: ModalType) => {
    setActiveModal(type);
    setModalLoading(true);
    try {
      if (type === 'present' || type === 'total') {
        const res = await dashboardService.getPresentEmployees();
        if (res.success) setPresentEmployees(res.data);
      }
      if (type === 'absent' || type === 'total') {
        const res = await dashboardService.getAbsentEmployees();
        if (res.success) setAbsentEmployees(res.data);
      }
      if (type === 'late') {
        const res = await dashboardService.getLateEmployees();
        if (res.success) setLateEmployees(res.data);
      }
    } catch (err) {
      console.error('Modal data load error:', err);
    } finally {
      setModalLoading(false);
    }
  };

  const closeModal = () => {
    setActiveModal(null);
  };

  // 2026-08-15 FIX: Alan adlari backend ile UYUSMUYORDU (presentCount vs present).
  // Sonuc: onTime/late/absent hepsi NaN oluyor, grafik 7 gun devamsizlik olmasina
  // ragmen her seyi "Zamaninda" gosteriyordu. Iki ismi de destekliyoruz.
  const chartTrendData = attendanceTrend.map(item => {
    const mevcut = item.presentCount ?? item.present ?? 0;
    const gec = item.lateCount ?? item.late ?? 0;
    const devamsiz = item.absentCount ?? item.absent ?? 0;
    const izinli = item.leaveCount ?? 0;
    return {
      day: new Date(item.date).toLocaleDateString('tr-TR', { weekday: 'short', day: 'numeric' }),
      // Gec gelenler zaten "mevcut" icinde sayiliyor; cift gostermemek icin cikariyoruz
      onTime: Math.max(0, mevcut - gec),
      late: gec,
      // 2026-08-15 (kullanici talebi): Izinliler ayri seri — onceden devamsizin
      // icinde eriyip grafik hep kirmizi gorunuyordu
      leave: izinli,
      absent: devamsiz
    };
  });

  const deptChartData = departments.map(d => ({
    name: d.departmentName?.substring(0, 6) || 'Dept',
    total: d.employeeCount,
    present: d.presentCount
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-slate-600">{t('dashboard.loading')}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-red-800 mb-2">{t('dashboard.error')}</h3>
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={loadDashboardData}
          className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
        >
          {t('dashboard.retry')}
        </button>
      </div>
    );
  }

  const totalEmployees = stats?.totalEmployees || 0;
  const presentToday = stats?.presentToday || 0;
  const lateToday = stats?.lateToday || 0;
  // 2026-08-15 (kullanici talebi): erken cikanlar — gec gelenlerle ayni kartta
  const earlyLeaveToday = stats?.earlyLeaveToday || 0;
  const absentToday = stats?.absentToday || 0;
  const onLeave = stats?.onLeave || 0;
  const pendingLeaves = stats?.pendingLeaves || 0;
  const attendanceRate = totalEmployees > 0 ? Math.round((presentToday / totalEmployees) * 100) : 0;

  const getModalTitle = () => {
    switch (activeModal) {
      case 'total': return t('dashboard.totalStaffModal', { count: totalEmployees });
      case 'present': return t('dashboard.presentTodayModal', { count: presentToday });
      case 'absent': return t('dashboard.absentMissingModal', { count: absentToday });
      case 'late': return t('dashboard.lateModal', { count: lateToday });
      default: return '';
    }
  };

  const getModalColor = () => {
    switch (activeModal) {
      case 'total': return 'bg-blue-600';
      case 'present': return 'bg-green-600';
      case 'absent': return 'bg-red-600';
      case 'late': return 'bg-amber-500';
      default: return 'bg-slate-600';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">{t('dashboard.operationalOverview')}</h1>
          <p className="text-slate-500 text-sm">{t('dashboard.realTimeMetrics')}</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={loadDashboardData}
            className="text-slate-500 hover:text-slate-700 p-2"
            title={t('dashboard.refresh')}
          >
            <TrendingUp size={18} />
          </button>
          <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span> {t('dashboard.liveSystem')}
          </span>
        </div>
      </div>

      <div className="flex gap-2">
        <button
          onClick={() => setTimeFilter('daily')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            timeFilter === 'daily'
              ? 'bg-blue-600 text-white'
              : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
          }`}
        >
          {t('dashboard.daily')}
        </button>
        <button
          onClick={() => setTimeFilter('weekly')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            timeFilter === 'weekly'
              ? 'bg-blue-600 text-white'
              : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
          }`}
        >
          {t('dashboard.weekly')}
        </button>
        <button
          onClick={() => setTimeFilter('monthly')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            timeFilter === 'monthly'
              ? 'bg-blue-600 text-white'
              : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
          }`}
        >
          {t('dashboard.monthly')}
        </button>

        {/* 2026-08-15 (kullanici talebi): tarih araligi secimi */}
        <div className="flex items-center gap-2 ml-auto flex-wrap">
          <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Tarih Aralığı</span>
          <input
            type="text"
            inputMode="numeric"
            placeholder="gg.aa.yyyy"
            maxLength={10}
            value={basMetin}
            onChange={(e) => {
              const v = tarihMaskele(e.target.value);
              setBasMetin(v);
              setOzelBas(trTarihiIso(v));
            }}
            className={`w-32 bg-white border rounded-lg px-3 py-1.5 text-sm font-mono text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              basMetin && !ozelBas ? 'border-red-300' : 'border-slate-200'
            }`}
          />
          <span className="text-slate-400">–</span>
          <input
            type="text"
            inputMode="numeric"
            placeholder="gg.aa.yyyy"
            maxLength={10}
            value={bitMetin}
            onChange={(e) => {
              const v = tarihMaskele(e.target.value);
              setBitMetin(v);
              setOzelBit(trTarihiIso(v));
            }}
            className={`w-32 bg-white border rounded-lg px-3 py-1.5 text-sm font-mono text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              bitMetin && !ozelBit ? 'border-red-300' : 'border-slate-200'
            }`}
          />
          {(basMetin || bitMetin) && (
            <button
              onClick={() => {
                setOzelBas(''); setOzelBit('');
                setBasMetin(''); setBitMetin('');
              }}
              className="text-xs font-bold text-slate-500 hover:text-slate-700 underline"
              title="Tarih aralığını temizle, dönem butonlarına dön"
            >
              Temizle
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
        <div
          className="bg-white p-5 rounded-xl shadow-sm border border-slate-200 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => openModal('total')}
        >
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">{t('dashboard.totalEmployees')}</p>
              <h3 className="text-2xl font-bold text-slate-800 mt-1">{totalEmployees}</h3>
            </div>
            <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
              <Users size={20} />
            </div>
          </div>
          <div className="mt-2 text-xs text-green-600 font-medium">{t('dashboard.activeStaffCount')}</div>
        </div>

        <div
          className="bg-white p-5 rounded-xl shadow-sm border border-slate-200 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => openModal('present')}
        >
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">{t('dashboard.present')}</p>
              <h3 className="text-2xl font-bold text-slate-800 mt-1">{presentToday}</h3>
            </div>
            <div className="p-2 bg-green-50 text-green-600 rounded-lg">
              <CheckCircle size={20} />
            </div>
          </div>
          <div className="mt-2 text-xs text-slate-400">{t('dashboard.attendancePercent', { rate: attendanceRate })}</div>
        </div>

        {/* 2026-08-15 (kullanici talebi): Gec gelenler ve erken cikanlar TEK kartta —
            ayri kart acilinca panel cok yer kapliyordu. */}
        <div
          className="bg-white p-5 rounded-xl shadow-sm border border-slate-200 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => openModal('late')}
          title="Geç gelen personel listesini aç"
        >
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                {t('dashboard.lateArrivalsLabel')} / Erken Çıkan
              </p>
              <div className="flex items-baseline gap-2 mt-1">
                <h3 className="text-2xl font-bold text-amber-600">{lateToday}</h3>
                <span className="text-slate-300 font-bold">/</span>
                <h3 className="text-2xl font-bold text-orange-600">{earlyLeaveToday}</h3>
              </div>
            </div>
            <div className="p-2 bg-amber-50 text-amber-600 rounded-lg">
              <Clock size={20} />
            </div>
          </div>
          <div className="mt-2 text-xs font-medium">
            <span className="text-amber-600">{lateToday} geç</span>
            <span className="text-slate-300 mx-1.5">·</span>
            <span className="text-orange-600">{earlyLeaveToday} erken çıkış</span>
          </div>
        </div>

        <div
          className="bg-white p-5 rounded-xl shadow-sm border border-slate-200 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => openModal('absent')}
        >
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">{t('dashboard.absence')}</p>
              <h3 className="text-2xl font-bold text-slate-800 mt-1">{absentToday}</h3>
            </div>
            <div className="p-2 bg-red-50 text-red-600 rounded-lg">
              <UserX size={20} />
            </div>
          </div>
          <div className="mt-2 text-xs text-red-600 font-medium">{absentToday > 0 ? t('dashboard.absentees') : t('dashboard.fullAttendance')}</div>
        </div>

        <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">{t('dashboard.deviceStatus')}</p>
              <h3 className="text-2xl font-bold text-slate-800 mt-1">{stats?.onlineDevices || 0}/{stats?.totalDevices || 0}</h3>
            </div>
            <div className="p-2 bg-indigo-50 text-indigo-600 rounded-lg">
              <Timer size={20} />
            </div>
          </div>
          <div className="mt-2 text-xs text-slate-400">{t('dashboard.onlineDevices')}</div>
        </div>

        {/* 2026-08-15 (kullanici): Kart tiklanmiyordu. Artik izin sayfasina gotururuyor;
            bekleyen talep varsa dogrudan "Bekliyor" filtresiyle acilir. */}
        <div
          onClick={() => navigate(pendingLeaves > 0 ? '/leaves?status=Pending' : '/leaves')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate(pendingLeaves > 0 ? '/leaves?status=Pending' : '/leaves');
            }
          }}
          title="İzin sayfasını aç"
          className="bg-white p-5 rounded-xl shadow-sm border border-slate-200 cursor-pointer hover:border-purple-300 hover:shadow-md transition-all focus:outline-none focus:ring-2 focus:ring-purple-400"
        >
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">{t('dashboard.onLeave')}</p>
              <h3 className="text-2xl font-bold text-slate-800 mt-1">{onLeave}</h3>
            </div>
            <div className="p-2 bg-purple-50 text-purple-600 rounded-lg">
              <Calendar size={20} />
            </div>
          </div>
          <div className={`mt-2 text-xs font-bold ${pendingLeaves > 0 ? 'text-amber-600' : 'text-slate-400'}`}>
            {t('dashboard.pendingRequests', { count: pendingLeaves })}
            {pendingLeaves > 0 && ' →'}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white p-6 rounded-xl shadow-sm border border-slate-200">
          <h3 className="font-semibold text-slate-800 mb-6 flex items-center gap-2">
            <TrendingUp size={18} className="text-slate-400" />
            {timeFilter === 'daily' ? t('dashboard.last7Days') : timeFilter === 'weekly' ? t('dashboard.last4Weeks') : t('dashboard.last3Months')} {t('dashboard.attendanceTrends')}
          </h3>
          <div className="h-72">
            {chartTrendData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chartTrendData}>
                  <XAxis dataKey="day" axisLine={false} tickLine={false} />
                  <YAxis axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: '#f1f5f9' }} contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} />
                  <Legend />
                  <Bar dataKey="onTime" name={t('dashboard.onTime')} stackId="a" fill="#3b82f6" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="late" name={t('dashboard.late')} stackId="a" fill="#f59e0b" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="leave" name="İzinli" stackId="a" fill="#a855f7" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="absent" name={t('dashboard.absent')} stackId="a" fill="#ef4444" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-slate-400">
                {t('dashboard.noDataYet')}
              </div>
            )}
          </div>
        </div>

        <div className="bg-gradient-to-br from-indigo-900 to-slate-900 p-6 rounded-xl shadow-lg text-white flex flex-col">
          <div className="flex items-center justify-between gap-2 mb-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="text-yellow-400" />
              <h3 className="font-semibold text-lg">Özet</h3>
            </div>
            {/* 2026-08-15 (kullanici): Kart "hic degismiyor" goruntusu veriyordu.
                Hangi donemin gosterildigi artik acikca yaziyor. */}
            <span className="text-[11px] font-bold uppercase tracking-wider bg-white/15 px-2.5 py-1 rounded-full">
              {basMetin || bitMetin
                ? `${basMetin || '...'} → ${bitMetin || '...'}`
                : timeFilter === 'daily' ? 'Bugün'
                : timeFilter === 'weekly' ? 'Son 7 Gün' : 'Son 30 Gün'}
            </span>
          </div>
          <div className="flex-1 bg-white/10 rounded-lg p-4 text-sm text-slate-200 backdrop-blur-sm">
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span>{t('dashboard.attendanceRate')}</span>
                <span className="font-bold text-green-400">{attendanceRate}%</span>
              </div>
              <div className="flex justify-between items-center">
                <span>{t('dashboard.presentStaff')}</span>
                <span className="font-bold text-blue-400">{presentToday} {t('dashboard.people')}</span>
              </div>
              <div className="flex justify-between items-center">
                <span>{t('dashboard.lateArrivalsLabel')}</span>
                <span className="font-bold text-amber-400">{lateToday} {t('dashboard.people')}</span>
              </div>
              <div className="flex justify-between items-center">
                <span>Erken Çıkan</span>
                <span className="font-bold text-orange-400">{earlyLeaveToday} {t('dashboard.people')}</span>
              </div>
              <div className="flex justify-between items-center">
                <span>{t('dashboard.absent')}</span>
                <span className="font-bold text-red-400">{absentToday} {t('dashboard.people')}</span>
              </div>
              <div className="flex justify-between items-center">
                <span>{t('dashboard.leaveStaff')}</span>
                <span className="font-bold text-purple-400">{onLeave} {t('dashboard.people')}</span>
              </div>
              <div className="pt-3 mt-1 border-t border-white/15 flex justify-between items-center">
                <span className="text-slate-300">Toplam Personel</span>
                <span className="font-bold text-white">{totalEmployees} {t('dashboard.people')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
          <h3 className="font-semibold text-slate-800 mb-4">{t('dashboard.departmentDistribution')}</h3>
          <div className="h-60">
            {deptChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={230}>
                <BarChart data={deptChartData} layout="vertical">
                  <XAxis type="number" hide />
                  <YAxis dataKey="name" type="category" width={50} axisLine={false} tickLine={false} />
                  <Tooltip />
                  <Bar dataKey="total" name={t('dashboard.total')} fill="#3b82f6" radius={[0, 4, 4, 0]} barSize={20} />
                  <Bar dataKey="present" name={t('dashboard.present')} fill="#10b981" radius={[0, 4, 4, 0]} barSize={20} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-slate-400">
                {t('dashboard.noDepartment')}
              </div>
            )}
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
          <h3 className="font-semibold text-slate-800 mb-4">{t('dashboard.departmentDetails')}</h3>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {departments.length > 0 ? departments.map((dept, index) => (
              <div key={index} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                <span className="text-sm font-medium text-slate-700">{dept.departmentName}</span>
                <div className="flex gap-2">
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">{dept.employeeCount} {t('dashboard.people')}</span>
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">{dept.presentCount} {t('dashboard.present')}</span>
                </div>
              </div>
            )) : (
              <div className="text-center text-slate-400 py-4">{t('dashboard.noDepartment')}</div>
            )}
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
          <h3 className="font-semibold text-slate-800 mb-4">{t('dashboard.notifications')}</h3>
          <div className="space-y-3">
            {pendingLeaves > 0 && (
              <div className="flex items-center p-3 bg-amber-50 rounded-lg border border-amber-100">
                <Calendar className="text-amber-500 mr-3" size={20} />
                <div className="flex-1">
                  <div className="text-sm font-medium text-amber-800">{t('dashboard.pendingLeaveRequests', { count: pendingLeaves })}</div>
                  <div className="text-xs text-amber-600">{t('dashboard.pendingApprovals')}</div>
                </div>
              </div>
            )}

            {lateToday > 0 && (
              <div className="flex items-center p-3 bg-red-50 rounded-lg border border-red-100">
                <AlertCircle className="text-red-500 mr-3" size={20} />
                <div className="flex-1">
                  <div className="text-sm font-medium text-red-800">{t('dashboard.lateStaffToday', { count: lateToday })}</div>
                  <div className="text-xs text-red-600">{t('dashboard.lateEntriesToday')}</div>
                </div>
              </div>
            )}

            <div className="flex items-center p-3 bg-blue-50 rounded-lg border border-blue-100">
              <Briefcase className="text-blue-500 mr-3" size={20} />
              <div className="flex-1">
                <div className="text-sm font-medium text-blue-800">{t('dashboard.systemActive')}</div>
                <div className="text-xs text-blue-600">{t('dashboard.devicesOnline', { count: stats?.onlineDevices || 0 })}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {activeModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={closeModal}>
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            <div className={`${getModalColor()} text-white px-6 py-4 rounded-t-xl flex items-center justify-between`}>
              <h3 className="text-lg font-semibold">{getModalTitle()}</h3>
              <button onClick={closeModal} className="text-white/80 hover:text-white">
                <X size={20} />
              </button>
            </div>

            <div className="overflow-y-auto flex-1 p-4">
              {modalLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                  <span className="ml-2 text-slate-500">{t('dashboard.loading')}</span>
                </div>
              ) : activeModal === 'late' ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2 px-3 text-slate-500 font-medium">#</th>
                      <th className="text-left py-2 px-3 text-slate-500 font-medium">{t('dashboard.employee')}</th>
                      <th className="text-left py-2 px-3 text-slate-500 font-medium">{t('dashboard.regNo')}</th>
                      <th className="text-left py-2 px-3 text-slate-500 font-medium">{t('dashboard.department')}</th>
                      <th className="text-left py-2 px-3 text-slate-500 font-medium">{t('dashboard.entry')}</th>
                      <th className="text-left py-2 px-3 text-slate-500 font-medium">{t('dashboard.delay')}</th>
                      <th className="text-left py-2 px-3 text-slate-500 font-medium">{t('dashboard.last7DaysCol')}</th>
                      <th className="text-left py-2 px-3 text-slate-500 font-medium">{t('dashboard.last30DaysCol')}</th>
                      <th className="text-center py-2 px-3 text-slate-500 font-medium">{t('dashboard.detail')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lateEmployees.map((emp, i) => {
                      const monthlyStats = employeeStats?.topLateEmployees.find(e => e.id === emp.id);
                      return (
                        <tr key={emp.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-2 px-3 text-slate-400">{i + 1}</td>
                          <td className="py-2 px-3 font-medium text-slate-800">{emp.fullName}</td>
                          <td className="py-2 px-3 text-slate-500">{emp.employeeCode || '-'}</td>
                          <td className="py-2 px-3 text-slate-500">{emp.departmentName || '-'}</td>
                          <td className="py-2 px-3 text-slate-600">{emp.entryTime}</td>
                          <td className="py-2 px-3">
                            <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded text-xs font-medium">
                              {emp.lateMinutes} {t('dashboard.min')}
                            </span>
                          </td>
                          <td className="py-2 px-3">
                            {monthlyStats ? (
                              <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs font-bold">
                                {t('dashboard.xLate', { count: monthlyStats.weeklyLateCount })}
                              </span>
                            ) : (
                              <span className="text-slate-400 text-xs">-</span>
                            )}
                          </td>
                          <td className="py-2 px-3">
                            {monthlyStats ? (
                              <span className="bg-orange-100 text-orange-700 px-2 py-0.5 rounded text-xs font-bold">
                                {t('dashboard.xLate', { count: monthlyStats.lateCount })}
                              </span>
                            ) : (
                              <span className="text-slate-400 text-xs">-</span>
                            )}
                          </td>
                          <td className="py-2 px-3 text-center">
                            <button
                              onClick={() => navigate(`/employee-stats?id=${emp.id}`)}
                              className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                              title={t('dashboard.detailedStats')}
                            >
                              <BarChart2 size={16} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {lateEmployees.length === 0 && (
                      <tr><td colSpan={9} className="py-8 text-center text-slate-400">{t('dashboard.noLateStaff')}</td></tr>
                    )}
                  </tbody>
                </table>
              ) : activeModal === 'absent' ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2 px-3 text-slate-500 font-medium">#</th>
                      <th className="text-left py-2 px-3 text-slate-500 font-medium">{t('dashboard.employee')}</th>
                      <th className="text-left py-2 px-3 text-slate-500 font-medium">{t('dashboard.regNo')}</th>
                      <th className="text-left py-2 px-3 text-slate-500 font-medium">{t('dashboard.department')}</th>
                      <th className="text-left py-2 px-3 text-slate-500 font-medium">{t('dashboard.last7DaysCol')}</th>
                      <th className="text-left py-2 px-3 text-slate-500 font-medium">{t('dashboard.last30DaysCol')}</th>
                      <th className="text-center py-2 px-3 text-slate-500 font-medium">{t('dashboard.detail')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {absentEmployees.map((emp, i) => {
                      const monthlyStats = employeeStats?.topAbsentEmployees.find(e => e.id === emp.id);
                      return (
                        <tr key={emp.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-2 px-3 text-slate-400">{i + 1}</td>
                          <td className="py-2 px-3 font-medium text-slate-800">{emp.fullName}</td>
                          <td className="py-2 px-3 text-slate-500">{emp.employeeCode || '-'}</td>
                          <td className="py-2 px-3 text-slate-500">{emp.departmentName || '-'}</td>
                          <td className="py-2 px-3">
                            {monthlyStats ? (
                              <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs font-bold">
                                {t('dashboard.days', { count: monthlyStats.weeklyAbsentCount })}
                              </span>
                            ) : (
                              <span className="text-slate-400 text-xs">-</span>
                            )}
                          </td>
                          <td className="py-2 px-3">
                            {monthlyStats ? (
                              <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded text-xs font-bold">
                                {t('dashboard.days', { count: monthlyStats.absentCount })}
                              </span>
                            ) : (
                              <span className="text-slate-400 text-xs">-</span>
                            )}
                          </td>
                          <td className="py-2 px-3 text-center">
                            <button
                              onClick={() => navigate(`/employee-stats?id=${emp.id}`)}
                              className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                              title={t('dashboard.detailedStats')}
                            >
                              <BarChart2 size={16} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {absentEmployees.length === 0 && (
                      <tr><td colSpan={7} className="py-8 text-center text-slate-400">{t('dashboard.noAbsentStaff')}</td></tr>
                    )}
                  </tbody>
                </table>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2 px-3 text-slate-500 font-medium">#</th>
                      <th className="text-left py-2 px-3 text-slate-500 font-medium">{t('dashboard.employee')}</th>
                      <th className="text-left py-2 px-3 text-slate-500 font-medium">{t('dashboard.regNo')}</th>
                      <th className="text-left py-2 px-3 text-slate-500 font-medium">{t('dashboard.department')}</th>
                      <th className="text-center py-2 px-3 text-slate-500 font-medium">{t('dashboard.detail')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const list = activeModal === 'present' ? presentEmployees
                        : [...presentEmployees, ...absentEmployees].sort((a, b) => a.fullName.localeCompare(b.fullName, 'tr'));
                      return list.map((emp, i) => (
                        <tr key={emp.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-2 px-3 text-slate-400">{i + 1}</td>
                          <td className="py-2 px-3 font-medium text-slate-800">{emp.fullName}</td>
                          <td className="py-2 px-3 text-slate-500">{emp.employeeCode || '-'}</td>
                          <td className="py-2 px-3 text-slate-500">{emp.departmentName || '-'}</td>
                          <td className="py-2 px-3 text-center">
                            <button
                              onClick={() => navigate(`/employee-stats?id=${emp.id}`)}
                              className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                              title={t('dashboard.detailedStats')}
                            >
                              <BarChart2 size={16} />
                            </button>
                          </td>
                        </tr>
                      ));
                    })()}
                    {((activeModal === 'present' && presentEmployees.length === 0) ||
                      (activeModal === 'total' && presentEmployees.length === 0 && absentEmployees.length === 0)) && (
                      <tr><td colSpan={5} className="py-8 text-center text-slate-400">{t('dashboard.noRecordsFound')}</td></tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
